/**
 * Admin API — only usable by users in settings.admin_ids.
 *
 * Mirrors `silentposter_backend/api/admin.py`.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type ApiError } from "./client";

// ============================================================
// Types
// ============================================================

export interface AdminOverview {
  total_users: number;
  blocked_users: number;
  active_subs: number;
  paid_users: number;
  revenue_usd: number;
  pending_payouts: number;
  new_users_today: number;
}

export interface AdminUserRow {
  id: number;
  username: string | null;
  first_name: string | null;
  plan_id: string | null;
  plan_expires_at: string | null;
  is_blocked: boolean;
  total_paid_usd: number;
  total_sent_messages: number;
  referrer_id: number | null;
  created_at: string;
}

export interface AdminUsersList {
  items: AdminUserRow[];
  total: number;
}

export interface AdminUserDetail extends AdminUserRow {
  referral_balance_usd: number;
  referral_total_earned_usd: number;
  referrals_count: number;
  accounts_count: number;
  chats_count: number;
}

export interface AdminPromo {
  id: number;
  code: string;
  plan_id: string;
  max_uses: number;
  used_count: number;
  created_at: string;
}

export interface AdminBroadcast {
  id: number;
  text_preview: string;
  segment: string;
  status: string;
  total: number;
  sent: number;
  failed: number;
  created_at: string;
}

export interface AdminBroadcastsList {
  items: AdminBroadcast[];
}

export type BroadcastSegment = "all" | "active" | "free" | "paid";
export type BroadcastParseMode = "HTML" | "MarkdownV2" | "None";

// ============================================================
// Overview
// ============================================================

export function useAdminOverview() {
  return useQuery<AdminOverview, ApiError>({
    queryKey: ["admin", "overview"],
    queryFn: () => api.get<AdminOverview>("/admin/overview"),
    staleTime: 30_000,
  });
}

// ============================================================
// Users
// ============================================================

export interface UsersFilter {
  q?: string;
  plan?: string;
  blocked?: boolean | null;
  limit?: number;
  offset?: number;
}

export function useAdminUsers(filter: UsersFilter = {}) {
  const params = new URLSearchParams();
  if (filter.q) params.set("q", filter.q);
  if (filter.plan) params.set("plan", filter.plan);
  if (filter.blocked != null) params.set("blocked", String(filter.blocked));
  params.set("limit", String(filter.limit ?? 50));
  params.set("offset", String(filter.offset ?? 0));
  const qs = params.toString();
  return useQuery<AdminUsersList, ApiError>({
    queryKey: ["admin", "users", qs],
    queryFn: () => api.get<AdminUsersList>(`/admin/users?${qs}`),
    staleTime: 15_000,
  });
}

export function useAdminUser(userId: number | null) {
  return useQuery<AdminUserDetail, ApiError>({
    queryKey: ["admin", "user", userId],
    queryFn: () => api.get<AdminUserDetail>(`/admin/users/${userId}`),
    enabled: userId != null,
    staleTime: 15_000,
  });
}

export function useBlockUser() {
  const qc = useQueryClient();
  return useMutation<AdminUserRow, ApiError, { userId: number; reason?: string }>({
    mutationFn: ({ userId, reason }) =>
      api.post<AdminUserRow>(`/admin/users/${userId}/block`, { reason: reason ?? null }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "user", vars.userId] });
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
    },
  });
}

export function useUnblockUser() {
  const qc = useQueryClient();
  return useMutation<AdminUserRow, ApiError, { userId: number }>({
    mutationFn: ({ userId }) => api.post<AdminUserRow>(`/admin/users/${userId}/unblock`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "user", vars.userId] });
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
    },
  });
}

export function useGrantPlan() {
  const qc = useQueryClient();
  return useMutation<
    AdminUserRow,
    ApiError,
    { userId: number; plan_id: string; duration_days?: number | null }
  >({
    mutationFn: ({ userId, plan_id, duration_days }) =>
      api.post<AdminUserRow>(`/admin/users/${userId}/grant-plan`, {
        plan_id,
        duration_days: duration_days ?? null,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "user", vars.userId] });
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
    },
  });
}

// ============================================================
// Promos
// ============================================================

export function useAdminPromos() {
  return useQuery<AdminPromo[], ApiError>({
    queryKey: ["admin", "promos"],
    queryFn: () => api.get<AdminPromo[]>("/admin/promos"),
    staleTime: 30_000,
  });
}

export function useCreatePromo() {
  const qc = useQueryClient();
  return useMutation<
    AdminPromo,
    ApiError,
    { code: string; plan_id: string; max_uses: number }
  >({
    mutationFn: (body) => api.post<AdminPromo>("/admin/promos", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "promos"] });
    },
  });
}

export function useDeletePromo() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { promoId: number }>({
    mutationFn: ({ promoId }) => api.delete<void>(`/admin/promos/${promoId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "promos"] });
    },
  });
}

// ============================================================
// Broadcasts
// ============================================================

export function useAdminBroadcasts(opts?: { refetchInterval?: number | false }) {
  return useQuery<AdminBroadcastsList, ApiError>({
    queryKey: ["admin", "broadcasts"],
    queryFn: () => api.get<AdminBroadcastsList>("/admin/broadcasts"),
    refetchInterval: opts?.refetchInterval ?? 5_000,
    staleTime: 0,
  });
}

export function useCreateBroadcast() {
  const qc = useQueryClient();
  return useMutation<
    AdminBroadcast,
    ApiError,
    { text: string; segment: BroadcastSegment; parse_mode: BroadcastParseMode }
  >({
    mutationFn: (body) => api.post<AdminBroadcast>("/admin/broadcasts", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "broadcasts"] });
    },
  });
}
